import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import styled from 'styled-components';

import { AuthProvider } from '@/features/AuthProvider/AuthProvider';
import { ProtectedRoute } from '@/features/ProtectedRoute/ProtectedRoute';
import { GlobalStyles, ThemeProvider } from '@/theme';
import { Flex } from '@/ui/Flex/Flex';

import useOnlineStatus from 'src/hooks/useOnlineStatus';
import { useAutoStartPlayer } from 'src/hooks/useAutoStartPlayer';
import { LanguageProvider } from '@/context/Language';
import { AdminEditPage } from './features/AdminEditPage/AdminEditPage';
import { ConfigsHomePage } from './features/ConfigsHomePage/ConfigsHomePage';
import { SlidesEditor } from './features/SlidesEditor/SlidesEditor';
import { Navbar } from './ui-components/Navbar/Navbar';
import { SlideRenderer } from './features/SlideRenderer/SlideRenderer';
import './sentry';
import { MockControls } from './features/MockControls';
import useStatisticsSaver from './features/_data/useStatisticsSaver';
import { getArgsValue } from '../utils/getArgsValue';
import { ConfigurationService } from '../services/configuration.service';
import LogicEditor from './features/LogicEditor/LogicEditor';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
    },
  },
});

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

export const AppShell = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.includes('/admin');
  const { toasts } = useToasterStore();
  const TOAST_LIMIT = 3;
  const [unitId, setUnitId] = useState<string | null>(null);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    const fetchUnitId = async () => {
      try {
        const id = await getArgsValue('UNIT_ID');
        setUnitId(id); // Update state when value is fetched
        if (id) ConfigurationService.setUnitId(id);
      } catch (error) {
        console.error('Failed to fetch UNIT_ID:', error);
      }
    };

    fetchUnitId();
  }, []);

  useStatisticsSaver();
  useAutoStartPlayer(unitId ?? undefined, isOnline);

  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  return unitId ? (
    <StyledWrapper>
      <Flex grow>
        <Outlet />
      </Flex>
    </StyledWrapper>
  ) : (
    <ProtectedRoute>
      <StyledWrapper>
        {!location.pathname.includes('/player') && (
          <Navbar isAdmin={isAdminPage} />
        )}

        <Outlet />
      </StyledWrapper>
    </ProtectedRoute>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppShell />} path="/">
      <Route element={<ConfigsHomePage isAdmin={false} />} index />
      <Route element={<SlidesEditor />} path="/upload/:id/*" />
      <Route element={<SlideRenderer />} path="/player/:id/*" />
      <Route element={<LogicEditor />} path="/upload/logic/:id/:title/*" />
      <Route
        element={
          <ProtectedRoute admin>
            <ConfigsHomePage isAdmin />
          </ProtectedRoute>
        }
        path="/admin"
      />
      <Route
        element={
          <ProtectedRoute admin>
            <AdminEditPage isAdmin />
          </ProtectedRoute>
        }
        path="/admin/edit/:id"
      />
    </Route>,
  ),
);

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
          <GlobalStyles />
          <Toaster />
          <RouterProvider router={router} />
          <MockControls />
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>
);
